// Lib
import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { identity } from 'lodash/fp';
import { connect } from 'react-redux';
import { List } from 'immutable';

// Components
import ElementReactions from '../reactions/ElementReactions';
import ColorSwatchContentEditable from './ColorSwatchContentEditable';
import SelectedElementColorPicker from '../../components/colors/SelectedElementColorPicker';

// Actions
import { setElementTypeAndUpdateElement } from '../../../common/elements/elementActions';
import { startEditingElement } from '../selection/selectionActions';

// Utils
import {
    getColor,
    getElementId,
    getMedia,
    getShowColorValue,
} from '../../../common/elements/utils/elementPropertyUtils';
import { getElementCardCropInset, getElementCardRenderedWidthPx } from '../utils/elementUtil';
import { prop } from '../../../common/utils/immutableHelper';
import getPaddingForAspectRatio from '../resizing/utils/getPaddingForAspectRatio';
import { getColorCssValue } from '../../../common/colors/colorHelper';
import { getHybridUseCase } from '../../hybridApp/hybridAppUtils';
import { HybridUseCase } from '../../hybridApp/hybridAppConstants';

// Constants
import { COLOR_SWATCH_COLOR_PICKER_EDITOR_KEY } from '../../../common/colorSwatches/colorSwatchesConstants';

const mapDispatchToProps = (dispatch, { element }) => ({
    dispatchUpdateElementType: ({ id, elementType, data }) => {
        dispatch(setElementTypeAndUpdateElement({ id, elementType, changes: data }));
    },
    dispatchStartEditingColorPicker: () =>
        dispatch(
            startEditingElement({
                id: getElementId(element),
                editorId: `${getElementId(element)}-${COLOR_SWATCH_COLOR_PICKER_EDITOR_KEY}`,
                editorKey: COLOR_SWATCH_COLOR_PICKER_EDITOR_KEY,
            }),
        ),
});

const shouldShowColorPicker = () => {
    switch (getHybridUseCase()) {
        case HybridUseCase.ANDROID_BOARD_LIST:
            // At the time of commit, the Android app displays an old Android picker that doesn't look good.
            // This might be re-enabled in the future once we find a way to replace the picker.
            return false;
        case HybridUseCase.IOS_BOARD_LIST:
        case HybridUseCase.IOS_CANVAS:
        case HybridUseCase.IPAD_OS:
        default:
            return true;
    }
};

const ColorSwatchRenderer = (props) => {
    const {
        element,
        gridSize,
        inList,
        tempMediaSize,
        registerMediaElement,
        dispatchStartEditingColorPicker,
        cropToGrid,
        isDragging,
    } = props;

    const colorPickerRef = useRef(null);

    const showColorPicker = useCallback((event) => {
        event.stopPropagation();

        if (colorPickerRef && colorPickerRef.current) colorPickerRef.current.focus();
    }, []);

    const color = getColor(element);
    const colorCssValue = getColorCssValue(color) || '#FFFFFF';

    // Ensure the rendered width is correct depending on whether in a column or on the canvas
    const widthPx = getElementCardRenderedWidthPx(props);
    const cropInset = getElementCardCropInset(inList);

    const savedSize = getMedia(element);

    const style = getPaddingForAspectRatio({
        savedSize,
        forcedSize: tempMediaSize,
        roundSaved: false,
        cropToGrid,
        cropInset,
        gridSize,
        widthPx,
    });

    const size = tempMediaSize || savedSize;
    const isSmallSize = prop('width', size) / gridSize < 15;
    const shouldShowColorValue = isSmallSize ? false : getShowColorValue(element);

    const wrapper = registerMediaElement || identity;

    // eslint-disable-next-line new-cap
    const selectedElements = List([element]);

    return (
        <>
            {!isDragging && (
                <SelectedElementColorPicker
                    selectedElements={selectedElements}
                    inputRef={colorPickerRef}
                    onOpen={dispatchStartEditingColorPicker}
                />
            )}

            {wrapper(
                <div
                    className="ColorSwatchRenderer"
                    style={{ ...style, background: colorCssValue }}
                    onDoubleClick={shouldShowColorPicker() ? showColorPicker : null}
                >
                    {shouldShowColorValue && <ColorSwatchContentEditable {...props} />}
                    <ElementReactions {...props} />
                </div>,
            )}
        </>
    );
};

ColorSwatchRenderer.propTypes = {
    element: PropTypes.object.isRequired,
    inList: PropTypes.string,
    gridSize: PropTypes.number,
    tempMediaSize: PropTypes.object,
    registerMediaElement: PropTypes.func,
    dispatchStartEditingColorPicker: PropTypes.func,
    cropToGrid: PropTypes.bool,
    isDragging: PropTypes.bool,
};

export default connect(null, mapDispatchToProps)(ColorSwatchRenderer);
